/**
 * The language picker (user menu, docs/contributing/i18n.md): each language in its own name, plus "Automatic" (the
 * browser's languages, then the company default). The choice is the user's (PATCH /v1/me `locale`) and follows them
 * to every browser. Pseudo-locales are listed in development and tests only, and never saved.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Languages } from "lucide-react";
import { useEffect, useId } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useToast } from "../components/ui";
import { devMode, useLocale, useT } from "./I18nProvider";
import { PSEUDO_LOCALES } from "./locales.gen.ts";
import { APP_LOCALES, forDashboard } from "./negotiate.ts";

const AUTO = "";

export function LanguagePicker({ me, companyDefault, compact }: { me: S["Me"]; companyDefault?: string | null; compact?: boolean }) {
  const t = useT();
  const id = useId();
  const toast = useToast();
  const qc = useQueryClient();
  const { locale, setLocale } = useLocale();
  const saved = me.preferences?.locale ?? null;
  const save = useMutation({
    mutationFn: (value: S["LocaleCode"] | null) => unwrap(api.PATCH("/v1/me", { body: { locale: value } })),
    onSuccess: (updated) => qc.setQueryData(["me"], updated),
  });
  const pseudo = devMode() && PSEUDO_LOCALES.some((p) => p.code === locale) ? locale : null;
  const value = pseudo ?? saved ?? AUTO;

  const choose = (next: string) => {
    if (PSEUDO_LOCALES.some((p) => p.code === next)) {
      setLocale(next); // development only: shown here, never stored on the account
      return;
    }
    const chosen = next === AUTO ? null : (next as S["LocaleCode"]);
    setLocale(chosen ?? forDashboard(null, navigator.languages ?? [], companyDefault ?? null));
    save.mutate(chosen, { onError: (err) => toast(errorMessage(err), true) });
  };

  return (
    <label className={compact ? "lang-picker compact" : "lang-picker"} htmlFor={id} title={t("shell.language.title")}>
      <Languages size={16} aria-hidden />
      <span className="sr-only">{t("shell.language.label")}</span>
      <select id={id} value={value} onChange={(e) => choose(e.target.value)} data-testid="language-picker">
        <option value={AUTO}>{t("shell.language.automatic")}</option>
        {APP_LOCALES.map((l) => (
          <option key={l.code} value={l.code} lang={l.code} dir={l.dir}>
            {l.name}
          </option>
        ))}
        {devMode() &&
          PSEUDO_LOCALES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.code} · {l.english}
            </option>
          ))}
      </select>
    </label>
  );
}

/** Applies the user's language once they are known, and again when it changes elsewhere: the setting, else the
 * browser's languages, else the company default. `?locale=` in development wins. */
export function useLocaleSync(user: string | null | undefined, company: string | null | undefined) {
  const { setLocale, forced } = useLocale();
  useEffect(() => {
    setLocale(forced ?? forDashboard(user, navigator.languages ?? [], company, devMode()));
  }, [user, company, forced, setLocale]);
}
